import React from 'react';
/**
 * DjayAvatar part — accessory layer (hand / head / face / neck / effects).
 *
 * This is the highest-churn extension surface: every unlockable cosmetic is a
 * `case` here. Verbatim extraction of the former `renderAccessory()`. To add a
 * new accessory: add its token to `DjayAccessory` (types.ts) + one `case`
 * below. The `uid` keeps gradient ids unique per rendered avatar.
 */
import type { DjayAccessory } from '../types';
interface AccessoryProps {
  accessory: DjayAccessory;
  uid: string;
  showAccessory: boolean;
}
export function Accessory({
  accessory,
  uid,
  showAccessory
}: AccessoryProps) {
  if (!showAccessory) return null;
  switch (accessory) {
    // HAND ACCESSORIES
    case 'seedling':
      return <g className="djay-accessory" transform="translate(165, 110) scale(1.5)" data-id="element-2979">
          <path d="M0 20 Q-5 10 -15 5 Q-5 15 0 20 Z" fill="#4ADE80" data-id="element-2980" />
          <path d="M0 20 Q5 10 15 5 Q5 15 0 20 Z" fill="#22C55E" data-id="element-2981" />
          <path d="M0 20 L0 30" stroke="#166534" strokeWidth="3" strokeLinecap="round" data-id="element-2982" />
        </g>;
    case 'pencil':
      return <g className="djay-accessory" transform="translate(165, 100) rotate(45) scale(1.5)" data-id="element-2983">
          <rect x="-5" y="0" width="10" height="30" fill="#FBBF24" rx="2" data-id="element-2984" />
          <path d="M-5 0 L0 -10 L5 0 Z" fill="#FCA5A5" data-id="element-2985" />
          <path d="M-2 -6 L0 -10 L2 -6 Z" fill="#1E293B" data-id="element-2986" />
          <rect x="-5" y="30" width="10" height="5" fill="#94A3B8" data-id="element-2987" />
          <rect x="-5" y="35" width="10" height="8" fill="#F87171" rx="2" data-id="element-2988" />
        </g>;
    case 'smallFlame':
      return <g className="djay-accessory" transform="translate(165, 110) scale(1.5)" data-id="element-2989">
          <path d="M0 20 C-10 20 -15 10 -5 0 C0 -10 5 -20 0 -25 C10 -15 15 -5 5 5 C10 10 10 20 0 20 Z" fill="#F97316" data-id="element-2990" />
          <path d="M0 15 C-5 15 -8 8 -2 0 C0 -5 2 -10 0 -12 C5 -7 8 -2 2 3 C5 5 5 15 0 15 Z" fill="#FBBF24" data-id="element-2991" />
        </g>;
    case 'trophy':
      return <g className="djay-accessory" transform="translate(165, 100) scale(1.5)" data-id="element-2992">
          <path d="M-15 0 L15 0 L10 20 L-10 20 Z" fill="#FBBF24" data-id="element-2993" />
          <path d="M-5 20 L5 20 L5 30 L-5 30 Z" fill="#D97706" data-id="element-2994" />
          <path d="M-12 30 L12 30 L12 35 L-12 35 Z" fill="#92400E" data-id="element-2995" />
          <path d="M-15 5 C-25 5 -25 15 -10 15" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" data-id="element-2996" />
          <path d="M15 5 C25 5 25 15 10 15" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" data-id="element-2997" />
        </g>;
    case 'starWand':
      return <g className="djay-accessory" transform="translate(165, 100) rotate(15) scale(1.5)" data-id="element-2998">
          <rect x="-2" y="0" width="4" height="40" fill="#D97706" rx="2" data-id="element-2999" />
          <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3000" />
          <path d="M0 -10 L2 -3 L9 -3 L4 2 L6 9 L0 4 L-6 9 L-4 2 L-9 -3 L-2 -3 Z" fill="#FEF08A" data-id="element-3001" />
        </g>;
    case 'scepter':
      return <g className="djay-accessory" transform="translate(165, 90) rotate(15) scale(1.5)" data-id="element-3002">
          <rect x="-3" y="0" width="6" height="50" fill="#FCD34D" rx="3" data-id="element-3003" />
          <circle cx="0" cy="-5" r="12" fill="#8B5CF6" data-id="element-3004" />
          <circle cx="0" cy="-5" r="8" fill="#A78BFA" data-id="element-3005" />
          <path d="M-15 -5 L15 -5 M0 -20 L0 10" stroke="#FCD34D" strokeWidth="3" data-id="element-3006" />
          <circle cx="0" cy="-5" r="3" fill="#FDE047" data-id="element-3007" />
        </g>;
    case 'cosmicOrb':
      return <g className="djay-accessory" transform="translate(158, 122) scale(1.4)" data-id="element-3008">
          <circle cx="0" cy="0" r="15" fill={`url(#${uid}-cosmic-orb-grad)`} data-id="element-3009" />
          <circle cx="0" cy="0" r="15" fill="none" stroke="#C084FC" strokeWidth="2" strokeDasharray="5 5" className="djay-spin" data-id="element-3010" />
          <circle cx="-5" cy="-5" r="3" fill="white" fillOpacity="0.8" data-id="element-3011" />
          <circle cx="6" cy="4" r="1.5" fill="white" fillOpacity="0.6" data-id="element-3012" />
          <path d="M-20 0 Q0 -10 20 0 Q0 10 -20 0 Z" fill="#E879F9" fillOpacity="0.4" transform="rotate(30)" data-id="element-3013" />
          <defs data-id="element-3014">
            <radialGradient id={`${uid}-cosmic-orb-grad`} cx="30%" cy="30%" r="70%" data-id="element-3015">
              <stop offset="0%" stopColor="#E879F9" data-id="element-3016" />
              <stop offset="50%" stopColor="#A855F7" data-id="element-3017" />
              <stop offset="100%" stopColor="#4C1D95" data-id="element-3018" />
            </radialGradient>
          </defs>
        </g>;
    case 'shadowOrb':
      return <g className="djay-accessory" transform="translate(158, 122) scale(1.4)" data-id="element-3019">
          <circle cx="0" cy="0" r="15" fill="#0F172A" data-id="element-3020" />
          <circle cx="0" cy="0" r="17" fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="8 4" className="djay-spin-reverse" data-id="element-3021" />
          <circle cx="-4" cy="-4" r="4" fill="#475569" fillOpacity="0.5" data-id="element-3022" />
          <path d="M-10 5 Q0 -5 10 5 Q0 15 -10 5 Z" fill="#1E293B" data-id="element-3023" />
          <circle cx="0" cy="0" r="20" fill="none" stroke="#EF4444" strokeWidth="1" strokeOpacity="0.5" data-id="element-3024" />
        </g>;
    // HEAD ACCESSORIES
    case 'doubleFlameCrown':
      return <g className="djay-accessory" transform="translate(100, 15) scale(1.5)" data-id="element-3025">
          <path d="M-20 20 L-10 0 L0 15 L10 0 L20 20 Z" fill="#F59E0B" data-id="element-3026" />
          <path d="M-15 20 L-10 5 L0 15 L10 5 L15 20 Z" fill="#FCD34D" data-id="element-3027" />
          <circle cx="-10" cy="-2" r="3" fill="#EF4444" data-id="element-3028" />
          <circle cx="10" cy="-2" r="3" fill="#EF4444" data-id="element-3029" />
        </g>;
    case 'cap':
      return <g className="djay-accessory" transform="translate(100, 25) scale(1.4)" data-id="element-3030">
          <path d="M-25 10 C-25 -10 25 -10 25 10 Z" fill="#3B82F6" data-id="element-3031" />
          <path d="M-35 10 L30 10" stroke="#2563EB" strokeWidth="4" strokeLinecap="round" data-id="element-3032" />
          <circle cx="0" cy="-5" r="3" fill="#60A5FA" data-id="element-3033" />
        </g>;
    // FACE ACCESSORIES
    case 'sunglasses':
      return <g className="djay-accessory" transform="translate(100, 95) scale(1.4)" data-id="element-3034">
          <rect x="-35" y="-10" width="30" height="20" rx="4" fill="#0F172A" data-id="element-3035" />
          <rect x="5" y="-10" width="30" height="20" rx="4" fill="#0F172A" data-id="element-3036" />
          <path d="M-5 -5 L5 -5" stroke="#0F172A" strokeWidth="4" data-id="element-3037" />
          <path d="M-35 -5 L-45 -10" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" data-id="element-3038" />
          <path d="M35 -5 L45 -10" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" data-id="element-3039" />
          {/* Reflections */}
          <path d="M-30 -5 L-15 -5" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" opacity="0.5" data-id="element-3040" />
          <path d="M10 -5 L25 -5" stroke="#F8FAFC" strokeWidth="2" strokeLinecap="round" opacity="0.5" data-id="element-3041" />
        </g>;
    // NECK ACCESSORIES
    case 'badge100':
      return <g className="djay-accessory" transform="translate(100, 162) scale(1.3)" data-id="element-3042">
          <path d="M-20 -20 L0 0 L20 -20" fill="none" stroke="#3B82F6" strokeWidth="6" data-id="element-3043" />
          <circle cx="0" cy="0" r="18" fill="#EF4444" data-id="element-3044" />
          <circle cx="0" cy="0" r="14" fill="none" stroke="#FEF08A" strokeWidth="2" strokeDasharray="4 2" data-id="element-3045" />
          <text x="0" y="5" fontFamily="sans-serif" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle" data-id="element-3046">
            100
          </text>
        </g>;
    case 'medal':
      return <g className="djay-accessory" transform="translate(100, 162) scale(1.3)" data-id="element-3047">
          <path d="M-20 -30 L0 0 L20 -30" fill="none" stroke="#3B82F6" strokeWidth="6" data-id="element-3048" />
          <path d="M-15 -30 L0 0 L15 -30" fill="none" stroke="#60A5FA" strokeWidth="2" data-id="element-3049" />
          <circle cx="0" cy="5" r="16" fill="#F59E0B" data-id="element-3050" />
          <circle cx="0" cy="5" r="12" fill="#FCD34D" data-id="element-3051" />
          <text x="0" y="10" fontFamily="sans-serif" fontSize="12" fontWeight="bold" fill="#92400E" textAnchor="middle" data-id="element-3052">
            1
          </text>
        </g>;
    case 'leafGarland':
      return <g className="djay-accessory" transform="translate(100, 130) scale(1.3)" data-id="element-3053">
          <path d="M-45 -10 Q0 30 45 -10" fill="none" stroke="#22C55E" strokeWidth="4" data-id="element-3054" />
          <path d="M-35 10 Q-40 5 -35 0 Q-30 5 -35 10 Z" fill="#4ADE80" data-id="element-3055" />
          <path d="M-15 18 Q-20 13 -15 8 Q-10 13 -15 18 Z" fill="#4ADE80" data-id="element-3056" />
          <path d="M5 20 Q0 15 5 10 Q10 15 5 20 Z" fill="#4ADE80" data-id="element-3057" />
          <path d="M25 18 Q20 13 25 8 Q30 13 25 18 Z" fill="#4ADE80" data-id="element-3058" />
          <path d="M40 8 Q35 3 40 -2 Q45 3 40 8 Z" fill="#4ADE80" data-id="element-3059" />
        </g>;
    case 'goldChain':
      return <g className="djay-accessory" transform="translate(100, 158) scale(1.4)" data-id="element-3060">
          <path d="M-30 -10 Q0 30 30 -10" fill="none" stroke="#F59E0B" strokeWidth="6" strokeLinecap="round" data-id="element-3061" />
          <path d="M-30 -10 Q0 30 30 -10" fill="none" stroke="#FCD34D" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 2" data-id="element-3062" />
          <circle cx="0" cy="10" r="8" fill="#F59E0B" data-id="element-3063" />
          <circle cx="0" cy="10" r="5" fill="#FCD34D" data-id="element-3064" />
        </g>;
    // EFFECTS (Background/Surrounding)
    case 'tripleStars':
      return <g className="djay-accessory" transform="translate(100, 20) scale(1.5)" data-id="element-3065">
          <g transform="translate(-35, 0) scale(0.7)" data-id="element-3066">
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3067" />
          </g>
          <g transform="translate(0, -15)" data-id="element-3068">
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3069" />
          </g>
          <g transform="translate(35, 0) scale(0.7)" data-id="element-3070">
            <path d="M0 -15 L4 -4 L15 -4 L6 3 L10 14 L0 7 L-10 14 L-6 3 L-15 -4 L-4 -4 Z" fill="#FDE047" data-id="element-3071" />
          </g>
        </g>;
    default:
      return null;
  }
}